import { ReactNode, SyntheticEvent, useMemo, useState, useEffect } from 'react';
import { Dialog } from '@components/dialog';
import { Box, Tab, Theme } from '@mui/material';
import { Tabs } from '@components/tabs';
import { useQuote } from '@services/quotes';
import Loading from '@components/Loading';
import { useSearchQuoteItemCoverages } from '@services/quoteItem';
import { AttributesTabContent } from '@pages/QuoteDetails/sidebarItems/tools/AttributesTabContent';
import { AttributesTabFooter } from '@pages/QuoteDetails/sidebarItems/tools/AttributesTabFooter';
import { useSearchQuoteRatings } from '@services/quoteRating';
import { getEngine } from '@lio/rule-engine';
import { GroupedQuote } from '@lio/rule-engine/utils/quote-details';
import { useSearchParams } from 'react-router-dom';
import { LazyJsonViewer } from '@components/json/LazyJsonViewer';
import { adjustFontSize } from '@theme/styleOverrides/typography';
import { useQuoteStore } from '@store/Quote/QuoteStore';
import { shallow } from 'zustand/shallow';

interface Props {
  isOpen: boolean;
  handleClose: () => void;
  quoteId: string;
}

export enum TechnicalDetailsTab {
  Details = 'details',
  Attributes = 'attributes',
  Coverages = 'coverages',
  ValidationErrors = 'validationErrors',
  RatingHistory = 'ratingHistory',
}

const tabValues = {
  [TechnicalDetailsTab.Details]: {
    label: 'Details',
  },

  [TechnicalDetailsTab.Attributes]: {
    label: 'Attributes',
  },

  [TechnicalDetailsTab.Coverages]: {
    label: 'Coverages',
  },
  [TechnicalDetailsTab.ValidationErrors]: {
    label: 'Validation Errors',
  },
  [TechnicalDetailsTab.RatingHistory]: {
    label: 'Rating History',
  },
};

const TAB_MAPPING: Record<TechnicalDetailsTab, string> = {
  [TechnicalDetailsTab.Details]: 'details',
  [TechnicalDetailsTab.Attributes]: 'attributes',
  [TechnicalDetailsTab.Coverages]: 'coverages',
  [TechnicalDetailsTab.ValidationErrors]: 'validation-errors',
  [TechnicalDetailsTab.RatingHistory]: 'rating-history',
};

// Reverse mapping for URL parameter to TechnicalDetailsTab
const REVERSE_TAB_MAPPING: Record<string, TechnicalDetailsTab> = Object.entries(TAB_MAPPING).reduce(
  (acc, [tab, param]) => {
    acc[param] = tab as TechnicalDetailsTab;
    return acc;
  },
  {} as Record<string, TechnicalDetailsTab>,
);

export const TechnicalDetailsPanel = ({
  isDialogMode = false,
  onTabChange,
  attributesSearchValue,
  attributesType,
  quoteId,
}: {
  isDialogMode?: boolean;
  onTabChange?: (tab: TechnicalDetailsTab) => void;
  attributesSearchValue?: string;
  attributesType?: 'all' | 'active';
  quoteId: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeTab, setActiveTab] = useState<TechnicalDetailsTab>(TechnicalDetailsTab.Attributes);

  const [structuredQuote] = useQuoteStore((state) => [state.structuredQuote], shallow);
  const isLoading = !structuredQuote;

  const { data: quoteItemCoveragesResult } = useSearchQuoteItemCoverages({
    quoteId,
  });

  // Fetch rating history for Rating History tab
  const { data: ratingHistoryData } = useSearchQuoteRatings({
    quoteId,
    enabled: activeTab === TechnicalDetailsTab.RatingHistory,
    params: {
      pageLimit: 100,
      pageOffset: 0,
      sort: '-createdAt', // Sort by newest first
    },
  });

  // State for attributes tab when not in dialog mode
  const [panelAttributesSearchValue, setPanelAttributesSearchValue] = useState('');
  const [panelAttributesType, setPanelAttributesType] = useState<'all' | 'active'>('all');

  // Handle URL parameter changes (only in non-dialog mode)
  useEffect(() => {
    if (!isDialogMode) {
      const queryString = searchParams.get('sidebarTab');
      if (queryString) {
        const mappedTab = REVERSE_TAB_MAPPING[queryString];
        if (mappedTab) {
          setActiveTab(mappedTab);
        }
      }
    }
  }, [searchParams, isDialogMode]);

  const handleChange = (_event: SyntheticEvent, selectedTab: TechnicalDetailsTab) => {
    setActiveTab(selectedTab);
    onTabChange?.(selectedTab);

    // Update URL parameters only in non-dialog mode
    if (!isDialogMode) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('sidebarTab', TAB_MAPPING[selectedTab]);
      setSearchParams(newParams);
    }
  };

  const handlePanelAttributeTypeChange = (type: 'all' | 'active') => {
    setPanelAttributesType(type);
  };

  const handlePanelSearchChange = (value: string) => {
    setPanelAttributesSearchValue(value);
  };

  const coveragesTabContent = useMemo(() => {
    if (!structuredQuote || !quoteItemCoveragesResult?.items) return undefined;
    const { quoteItems } = structuredQuote;
    const coverages: Record<string, any> = {};
    quoteItems.forEach((qi) => {
      const { quoteItemType, instanceNumber, id, policyLifetimeId, parentId } = qi;
      const idx = `${quoteItemType.codeName}${instanceNumber ? `-${instanceNumber}` : ''}`;
      const quoteItemCoverages: Record<string, any> = {};
      quoteItemCoveragesResult.items.forEach((qic) => {
        if (qic.quoteItemId === id) {
          const idx = `${qic.coverage.coverageName}`;
          const attributes: Record<string, any> = {};
          qic.quoteItemCoverageAttributes.forEach((qica) => {
            const idx = `${qica.coverageAttributeType.coverageAttributeCategory}`;
            attributes[idx] = { ...qica };
          });
          const { coverage, effectiveOn, effectiveThrough, id, isIncludedInPremium, termFactor } = qic;
          quoteItemCoverages[idx] = {
            attributes,
            coverage,
            effectiveOn,
            effectiveThrough,
            id,
            isIncludedInPremium,
            termFactor,
          };
        }
      });
      const newQuoteItem = {
        id,
        instanceNumber,
        policyLifetimeId,
        parentId,
        quoteItemCoverages,
      };
      coverages[idx] = newQuoteItem;
    });
    return coverages;
  }, [structuredQuote, quoteItemCoveragesResult?.items]);

  const tabContent = useMemo(() => {
    let value;
    let Component: ReactNode | undefined;
    let Footer: ReactNode | undefined;
    switch (activeTab) {
      case TechnicalDetailsTab.Details: {
        const details = { ...structuredQuote };
        delete details.quoteItems;
        value = details;
        break;
      }
      case TechnicalDetailsTab.Attributes: {
        Component = (
          <AttributesTabContent
            quoteId={quoteId}
            isDialogMode={isDialogMode}
            searchValue={isDialogMode ? attributesSearchValue || '' : panelAttributesSearchValue}
            attributeType={isDialogMode ? attributesType || 'all' : panelAttributesType}
          />
        );
        Footer = (
          <AttributesTabFooter
            onAttributeTypeChange={handlePanelAttributeTypeChange}
            onSearchChange={handlePanelSearchChange}
            initialAttributeType={panelAttributesType}
            quoteId={quoteId}
          />
        );
        break;
      }
      case TechnicalDetailsTab.Coverages: {
        value = coveragesTabContent;
        break;
      }
      case TechnicalDetailsTab.ValidationErrors: {
        const ruleEngine = getEngine(structuredQuote as GroupedQuote);
        if (structuredQuote && !isLoading) {
          const errors = ruleEngine?.validateQuote(structuredQuote, { includeQuoteItemRules: true });

          Component = (
            <LazyJsonViewer
              value={errors}
              defaultInspectDepth={5}
              objectSortKeys
              sx={{
                fontSize: ({ palette }: Theme) => `${adjustFontSize(11, palette.fontSize)}px`,
                fontWeight: 300,
                backgroundColor: 'white',
              }}
            />
          );
        }
        value = {};
        break;
      }
      case TechnicalDetailsTab.RatingHistory: {
        // Display all rating history data, sorted newest to oldest
        const allRatings =
          ratingHistoryData?.pages.flatMap((page) =>
            page.items?.map((i) => {
              return {
                createdAt: i.createdAt,
                rateVersion: i.rateVersion,
                status: i.status,
                ratedByUser: {
                  id: i.ratedByUser.id,
                  firstName: i.ratedByUser.firstName,
                  lastName: i.ratedByUser.lastName,
                },
                totalPremium: i.totalPremium,
                totalTaxFeeSurcharge: i.totalTaxFeeSurcharge,
                termPremium: i.termPremium,
                termTaxFeeSurcharge: i.termTaxFeeSurcharge,
                endorsementDetails: i.endorsementDetails
                  ? {
                      basePolicyCost: i.endorsementDetails.basePolicyCost,
                      priorTransactionRetermedPremium: i.endorsementDetails.priorTransactionRetermedPremium,
                      priorPolicyCost: i.endorsementDetails.priorPolicyCost,
                      newPolicyCost: i.endorsementDetails.newPolicyCost,
                    }
                  : null,
              };
            }),
          ) || [];
        value = allRatings;
        break;
      }
      default: {
        value = {};
        break;
      }
    }

    //Remap Dates to ISO strings or viewer will display in local time
    let obj = value;
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      for (let [key, v] of Object.entries(obj)) {
        if (Object.prototype.toString.call(v) === '[object Date]') {
          (obj as Record<string, any>)[key] = v.toISOString();
        }
      }
    }

    return { value, Component, Footer };
  }, [
    activeTab,
    structuredQuote,
    quoteId,
    coveragesTabContent,
    isLoading,
    isDialogMode,
    attributesSearchValue,
    attributesType,
    panelAttributesSearchValue,
    panelAttributesType,
    ratingHistoryData,
  ]);

  return (
    <Box
      sx={{
        height: '100%', // Use full available height
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
    >
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <Box
            sx={{
              pl: '16px',
              flexShrink: 0,
              ...(isDialogMode && {
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1,
                backgroundColor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }),
            }}
          >
            <Tabs type='mini' disableRipple value={activeTab} onChange={handleChange}>
              <Tab
                label={tabValues[TechnicalDetailsTab.Details].label}
                value={TechnicalDetailsTab.Details}
                tabIndex={0}
              />
              <Tab
                label={tabValues[TechnicalDetailsTab.Attributes].label}
                value={TechnicalDetailsTab.Attributes}
                tabIndex={1}
              />
              <Tab
                label={tabValues[TechnicalDetailsTab.Coverages].label}
                value={TechnicalDetailsTab.Coverages}
                tabIndex={2}
              />
              <Tab
                label={tabValues[TechnicalDetailsTab.ValidationErrors].label}
                value={TechnicalDetailsTab.ValidationErrors}
                tabIndex={3}
              />
              <Tab
                label={tabValues[TechnicalDetailsTab.RatingHistory].label}
                value={TechnicalDetailsTab.RatingHistory}
                tabIndex={4}
              />
            </Tabs>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              padding: '15px',
              minHeight: 0,
            }}
          >
            {tabContent.Component ? (
              tabContent.Component
            ) : (
              <LazyJsonViewer
                value={tabContent}
                defaultInspectDepth={3}
                objectSortKeys
                sx={{
                  fontSize: ({ palette }: Theme) => `${adjustFontSize(11, palette.fontSize)}px`,
                  fontWeight: 300,
                  backgroundColor: 'white',
                }}
              />
            )}
          </Box>
          <Box>{tabContent.Footer && !isDialogMode ? tabContent.Footer : undefined}</Box>
        </>
      )}
    </Box>
  );
};

export const TechnicalDetailsModal = ({ isOpen, handleClose, quoteId }: Props) => {
  const { quote } = useQuote(quoteId!);

  // State for attributes tab
  const [attributesSearchValue, setAttributesSearchValue] = useState('');
  const [attributesType, setAttributesType] = useState<'all' | 'active'>('all');
  const [activeTab, setActiveTab] = useState<TechnicalDetailsTab>(TechnicalDetailsTab.Attributes);

  const handleAttributeTypeChange = (type: 'all' | 'active') => {
    setAttributesType(type);
  };

  const handleSearchChange = (value: string) => {
    setAttributesSearchValue(value);
  };

  const showPrimaryButton = activeTab !== TechnicalDetailsTab.Attributes;

  return (
    <Dialog
      id='technical-details-modal'
      handleCloseDialog={handleClose}
      open={isOpen}
      primaryBtnText={showPrimaryButton ? 'Close' : undefined}
      primaryBtnAction={showPrimaryButton ? handleClose : undefined}
      size='large'
      showBorders={false}
      title={`Quote #${quote?.policyNumber?.policyNumber ?? ''} Technical Details`}
      footerContent={
        activeTab === TechnicalDetailsTab.Attributes ? (
          <AttributesTabFooter
            isDialogMode={true}
            onAttributeTypeChange={handleAttributeTypeChange}
            onSearchChange={handleSearchChange}
            initialAttributeType={attributesType}
            onClose={handleClose}
            quoteId={quoteId}
          />
        ) : undefined
      }
    >
      <TechnicalDetailsPanel
        isDialogMode={true}
        onTabChange={setActiveTab}
        attributesSearchValue={attributesSearchValue}
        attributesType={attributesType}
        quoteId={quoteId}
      />
    </Dialog>
  );
};
